import type { AxiosResponse } from 'axios'
import type {
  AuthMeResponse,
  SecureLinkRequestPayload,
  SecureLinkResponse,
  SigninRequestPayload,
  SigninResponse,
} from '@/types/auth'
import type { User } from '@/types/user'
import { useAuthStore } from '@/stores/authStore'
import { ApiClient } from './apiClient'

class AuthService extends ApiClient {
  async sendSecureLink(
    payload: SecureLinkRequestPayload,
  ): Promise<AxiosResponse<SecureLinkResponse>> {
    return this.post<SecureLinkResponse>('/send-secure-link', payload)
  }

  async exchangeTokenForJWT(
    payload: SigninRequestPayload,
  ): Promise<AxiosResponse<SigninResponse>> {
    return this.post<SigninResponse>('/signin', payload)
  }

  async fetchCurrentUser(): Promise<User> {
    const response = await this.get<User>('/users/me', { version: 'v1' })
    return response.data
  }

  async getRBACRoles(userId: string, options?: { params?: Record<string, any> }): Promise<string[]> {
    const params = options?.params ? new URLSearchParams(options.params) : undefined
    const response = await this.get<string[]>(`/auth/getRBACRoles/${userId}${params ? `?${params.toString()}` : ''}`, { version: 'v2' })
    return response.data
  }

  /**
   * Batch variant: resolve RBAC roles for many users in a single request,
   * returning a `{ [userId]: roles[] }` map. Avoids the N+1 of one call per
   * row in list views (e.g. the org members table).
   */
  async getRBACRolesBatch(
    userIds: string[],
    options?: { params?: Record<string, any> },
  ): Promise<Record<string, string[]>> {
    if (!userIds.length) return {}
    const params = new URLSearchParams({ ...(options?.params ?? {}) })
    params.set('userIds', userIds.join(','))
    try {
      const response = await this.get<Record<string, string[]>>(
        `/auth/getRBACRoles?${params.toString()}`,
        { version: 'v2' },
      )
      return response.data ?? {}
    } catch {
      // Backend without the batch route (older/un-restarted build) → degrade
      // to one call per user so role-dependent UI (badges, the org-leader
      // replace control) still works. Reintroduces the N+1 only in that
      // fallback case.
      const entries = await Promise.all(
        userIds.map(async (id) => {
          try {
            return [id, await this.getRBACRoles(id, options)] as const
          } catch {
            return [id, [] as string[]] as const
          }
        }),
      )
      return Object.fromEntries(entries)
    }
  }

  /**
   * Returns the authenticated user's ID by decoding the JWT stored in
   * authStore. Returns null when there is no token or the token is
   * malformed - callers should treat null as "not signed in" and skip
   * any user-scoped operation rather than throwing. Reading from the
   * JWT (rather than userStore) avoids a circular waiting on
   * /auth/me hydration before we can issue RBAC lookups.
   */
  getCurrentUserId(): string | null {
    const token = useAuthStore().token
    if (!token) return null
    try {
      const [, payload] = token.split('.')
      if (!payload) return null
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
      const padding = normalized.length % 4 === 0 ? '' : '='.repeat(4 - (normalized.length % 4))
      const decoded = atob(normalized + padding)
      const data = JSON.parse(decoded) as { sub?: string }
      return data.sub ?? null
    } catch {
      return null
    }
  }

  /**
   * Fetches the authenticated user from /v2/auth/me. Each entry in `roles`
   * carries its own `context` map so the client can tell which entities a
   * given role applies to (e.g. `team_leader` -> `{ teams: [...] }`).
   */
  async fetchMe(): Promise<AuthMeResponse> {
    const response = await this.get<AuthMeResponse>('/auth/me', { version: 'v2' })
    return response.data
  }

  /**
   * Records acceptance of all three current legal documents (Terms of
   * Service, Privacy Policy, DPA) in one atomic step (POST
   * /v2/auth/accept-terms). Called only from TermsAndConditionsModal's
   * "Aceptar" button - the backend is the source of truth for whether this
   * counts, not anything set locally. `language` is the language the three
   * documents were PRESENTED in (the org's language at that moment),
   * recorded as evidence of what was actually read.
   */
  async acceptTerms(language: string): Promise<{ termsAccepted: boolean; termsAcceptedAt: string }> {
    const response = await this.post<{ termsAccepted: boolean; termsAcceptedAt: string }>(
      '/auth/accept-terms',
      { language },
      { version: 'v2' },
    )
    return response.data
  }
}

export const authService = new AuthService()

// Backwards-compatible function exports - keep the positional-arg shape
// the old useAuth/UserConnectView call sites expect, but route through
// the typed payload internally. Adopting the payload form everywhere is
// a follow-up; both shapes are valid for now.
export const sendSecureLink = (
  email: string,
  language: SecureLinkRequestPayload['language'],
) => {
  return authService.sendSecureLink({ email, language })
}

export const exchangeTokenForJWT = (
  token: string,
  language: SigninRequestPayload['language'],
) => {
  return authService.exchangeTokenForJWT({ token, language })
}

export const fetchCurrentUser = () => {
  return authService.fetchCurrentUser()
}

export const fetchMe = () => {
  return authService.fetchMe()
}
